import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  BellRing,
  CheckCheck,
  Monitor,
  AlertCircle,
  HelpCircle,
  Package,
  Settings,
  X,
  Loader2,
  Check,
  XCircle,
  WifiOff,
  Shield,
  Clock,
  AlertTriangle,
  Headphones,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useNotifications, Notification } from '@/hooks/useNotifications';
import { DeviceApprovalDialog } from '@/components/device/DeviceApprovalDialog';
import { RegisterLimitDialog, isRegisterLimitError } from '@/components/device/RegisterLimitDialog';
import { useDeviceApprovalRequests, useDeviceRegistration } from '@/hooks/useDeviceRegistration';
import { toast } from 'sonner';
import { usePendingApprovals, type PendingApproval } from '@/hooks/usePendingApprovals';
import { useAuth } from '@/hooks/useAuth';
import { useSupportRequestNotifications } from '@/hooks/useRemoteSession';
import { formatDistanceToNow } from 'date-fns';

// Format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Get icon for notification type
function getNotificationIcon(type: string) {
  switch (type) {
    case 'device_approval_request':
      return <Monitor className="h-4 w-4" />;
    case 'assistance_request':
      return <HelpCircle className="h-4 w-4" />;
    case 'low_stock':
      return <Package className="h-4 w-4" />;
    case 'order_issue':
      return <AlertCircle className="h-4 w-4" />;
    case 'system':
      return <Settings className="h-4 w-4" />;
    case 'terminal_offline_alert':
      return <WifiOff className="h-4 w-4" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
}

// Get color for notification type
function getNotificationColor(type: string) {
  switch (type) {
    case 'device_approval_request':
      return 'bg-blue-500/10 text-blue-600';
    case 'assistance_request':
      return 'bg-amber-500/10 text-amber-600';
    case 'low_stock':
      return 'bg-orange-500/10 text-orange-600';
    case 'order_issue':
      return 'bg-red-500/10 text-red-600';
    case 'terminal_offline_alert':
      return 'bg-red-500/10 text-red-600';
    default:
      return 'bg-gray-500/10 text-gray-600';
  }
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [selectedApprovalRequest, setSelectedApprovalRequest] = useState<any>(null);
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);
  const [registerLimitError, setRegisterLimitError] = useState<string | null>(null);
  const [browserNotificationPermission, setBrowserNotificationPermission] = useState<NotificationPermission | 'unsupported'>('default');
  const { profile } = useAuth();
  const navigate = useNavigate();

  // Check browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setBrowserNotificationPermission(window.Notification.permission);
    } else {
      setBrowserNotificationPermission('unsupported');
    }
  }, []);

  const requestBrowserNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await window.Notification.requestPermission();
      setBrowserNotificationPermission(permission);
      if (permission === 'granted') {
        toast.success('Browser notifications enabled', {
          description: 'You will now receive desktop notifications for alerts.',
        });
      }
    }
  };

  const {
    notifications,
    unreadCount,
    isLoading,
    canViewNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  // Fetch pending device approval requests
  const { data: pendingDeviceRequests } = useDeviceApprovalRequests();
  const { approveRequest, denyRequest } = useDeviceRegistration();

  // Fetch pending 2FA approvals for owners/primary_admins
  const {
    pendingApprovals,
    pendingCount: pending2FACount,
    approve: approve2FA,
    deny: deny2FA,
    isApproving: isApproving2FA,
    isDenying: isDenying2FA,
    formatActionType,
    getActionDescription,
  } = usePendingApprovals();

  // Fetch support requests from cashiers (for admins/managers)
  const { newRequests: supportRequests, dismissRequest: dismissSupportRequest } = useSupportRequestNotifications();

  // State for 2FA approval verification
  const [selected2FAApproval, setSelected2FAApproval] = useState<PendingApproval | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState<string | null>(null);

  // Check if user can view 2FA approvals
  const can2FAApprove = profile?.role === 'owner' || profile?.role === 'primary_admin';
  
  // Check if user can view support requests
  const canViewSupportRequests = ['superadmin', 'primary_admin', 'owner', 'admin', 'manager'].includes(profile?.role || '');

  if (!canViewNotifications) {
    return null;
  }

  // Combine notifications with pending device requests, 2FA approvals, and support requests for the bell count
  const pendingDeviceCount = pendingDeviceRequests?.length ?? 0;
  const supportRequestCount = canViewSupportRequests ? (supportRequests?.length ?? 0) : 0;
  const totalUnread = unreadCount + pendingDeviceCount + (can2FAApprove ? pending2FACount : 0) + supportRequestCount;

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    if (!notification.read_at) {
      markAsRead.mutate(notification.id);
    }

    // Handle device approval request
    if (notification.type === 'device_approval_request' && notification.data?.request_id) {
      const request = pendingDeviceRequests?.find(
        r => r.id === notification.data?.request_id
      );
      if (request) {
        setSelectedApprovalRequest(request);
        setOpen(false);
      }
    }
  };

  const handleDeviceRequestClick = (request: any) => {
    setSelectedApprovalRequest(request);
    setOpen(false);
  };

  const handleQuickApprove = async (e: React.MouseEvent, request: any) => {
    e.stopPropagation();
    setProcessingRequestId(request.id);
    try {
      await approveRequest.mutateAsync({
        requestId: request.id,
        deviceName: request.requested_device_name || undefined,
      });
      // Delete the corresponding notification if it exists
      const relatedNotification = notifications.find(
        n => n.type === 'device_approval_request' && n.data?.request_id === request.id
      );
      if (relatedNotification) {
        deleteNotification.mutate(relatedNotification.id);
      }
      toast.success('Device approved', {
        description: `The device has been registered for ${request.location?.name || 'the selected location'}.`,
      });
    } catch (error: any) {
      // Check if this is a register limit error
      if (isRegisterLimitError(error)) {
        setOpen(false);
        setRegisterLimitError(error.message);
      } else {
        toast.error('Failed to approve', {
          description: error.message || 'Please try again.',
        });
      }
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleQuickDecline = async (e: React.MouseEvent, request: any) => {
    e.stopPropagation();
    setProcessingRequestId(request.id);
    try {
      await denyRequest.mutateAsync({
        requestId: request.id,
        notes: 'Declined from notification',
      });
      // Delete the corresponding notification if it exists
      const relatedNotification = notifications.find(
        n => n.type === 'device_approval_request' && n.data?.request_id === request.id
      );
      if (relatedNotification) {
        deleteNotification.mutate(relatedNotification.id);
      }
      toast.success('Request declined', {
        description: 'The device registration request has been denied.',
      });
    } catch (error: any) {
      toast.error('Failed to decline', {
        description: error.message || 'Please try again.',
      });
    } finally {
      setProcessingRequestId(null);
    }
  };

  // 2FA Approval handlers
  const handle2FAApprove = async () => {
    if (!selected2FAApproval || !verificationCode) return;

    setVerificationError(null);
    try {
      await approve2FA({ approvalId: selected2FAApproval.id, code: verificationCode });
      setSelected2FAApproval(null);
      setVerificationCode('');
      toast.success('Approved', {
        description: 'The action has been approved successfully.',
      });
    } catch (err) {
      setVerificationError(err instanceof Error ? err.message : 'Failed to approve');
    }
  };

  const handle2FADeny = async (approval: PendingApproval) => {
    try {
      await deny2FA(approval.id);
      toast.success('Denied', {
        description: 'The action has been denied.',
      });
    } catch (err) {
      console.error('Failed to deny:', err);
      toast.error('Failed to deny', {
        description: 'Please try again.',
      });
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
          >
            <Bell className="h-5 w-5 text-muted-foreground" />
            {totalUnread > 0 && (
              <Badge
                variant="destructive"
                className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 text-xs"
              >
                {totalUnread > 99 ? '99+' : totalUnread}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          className="w-80 sm:w-96 p-0"
          sideOffset={8}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold">Notifications</h3>
            <div className="flex items-center gap-2">
              {browserNotificationPermission === 'default' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto py-1 px-2 text-xs gap-1"
                  onClick={requestBrowserNotificationPermission}
                >
                  <BellRing className="h-3 w-3" />
                  Enable
                </Button>
              )}
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto py-1 px-2 text-xs gap-1"
                  onClick={() => markAllAsRead.mutate()}
                  disabled={markAllAsRead.isPending}
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </Button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (pendingDeviceRequests?.length === 0 && notifications.length === 0 && (!can2FAApprove || pendingApprovals.length === 0) && supportRequestCount === 0) ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Bell className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div className="divide-y">
                {/* Pending 2FA Approvals - Show at top for owners/primary_admins */}
                {can2FAApprove && pendingApprovals.map((approval) => (
                  <div
                    key={`2fa-${approval.id}`}
                    className="p-4 bg-amber-50/50 dark:bg-amber-950/20"
                  >
                    {selected2FAApproval?.id === approval.id ? (
                      // Verification Form
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              Enter your 2FA code
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {getActionDescription(approval)}
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              setSelected2FAApproval(null);
                              setVerificationCode('');
                              setVerificationError(null);
                            }}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {verificationError && (
                          <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400 flex items-center gap-2">
                            <AlertTriangle className="h-3 w-3" />
                            {verificationError}
                          </div>
                        )}

                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={verificationCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setVerificationCode(value);
                          }}
                          placeholder="000000"
                          className="w-full px-3 py-2 text-center text-lg font-mono tracking-widest border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-green-500"
                          autoFocus
                        />

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handle2FAApprove}
                            disabled={verificationCode.length !== 6 || isApproving2FA}
                            className="flex-1 gap-1 bg-green-600 hover:bg-green-700"
                          >
                            {isApproving2FA ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="h-4 w-4" />
                                Approve
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handle2FADeny(approval)}
                            disabled={isDenying2FA}
                          >
                            Deny
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Approval Card
                      <div>
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-amber-500/10 text-amber-600">
                            <Shield className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                {formatActionType(approval.action_type)}
                              </span>
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {formatDistanceToNow(new Date(approval.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                              {getActionDescription(approval)}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Requested by {approval.requester_name || 'Unknown'}
                            </p>

                            {/* Expiry warning */}
                            {new Date(approval.expires_at) < new Date(Date.now() + 60000) && (
                              <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Expires in less than 1 minute
                              </div>
                            )}

                            <div className="flex items-center gap-2 mt-2">
                              <Button
                                size="sm"
                                className="h-7 px-3 text-xs gap-1 bg-green-600 hover:bg-green-700"
                                onClick={() => setSelected2FAApproval(approval)}
                              >
                                <Check className="h-3 w-3" />
                                Approve with 2FA
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-3 text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handle2FADeny(approval)}
                                disabled={isDenying2FA}
                              >
                                <XCircle className="h-3 w-3" />
                                Deny
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Pending Device Approval Requests - Show at top */}
                {pendingDeviceRequests?.map((request) => {
                  const requesterName = request.requester
                    ? `${request.requester.first_name || ''} ${request.requester.last_name || ''}`.trim()
                    : 'Unknown';
                  const isProcessing = processingRequestId === request.id;

                  return (
                    <div
                      key={`device-${request.id}`}
                      className={cn(
                        'w-full flex items-start gap-3 p-4 text-left transition-colors',
                        'bg-blue-50/50 dark:bg-blue-950/20'
                      )}
                    >
                      <div className={cn(
                        'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
                        'bg-blue-500/10 text-blue-600'
                      )}>
                        <Monitor className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-sm">Device Approval Request</p>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {formatRelativeTime(request.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5 truncate">
                          {requesterName} wants to register a device at {request.location?.name}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="default"
                            className="h-7 px-3 text-xs gap-1 bg-green-600 hover:bg-green-700"
                            onClick={(e) => handleQuickApprove(e, request)}
                            disabled={isProcessing}
                          >
                            {isProcessing && approveRequest.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Check className="h-3 w-3" />
                            )}
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-3 text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => handleQuickDecline(e, request)}
                            disabled={isProcessing}
                          >
                            {isProcessing && denyRequest.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <XCircle className="h-3 w-3" />
                            )}
                            Decline
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs text-muted-foreground"
                            onClick={() => handleDeviceRequestClick(request)}
                            disabled={isProcessing}
                          >
                            Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pending Support Requests from Cashiers */}
                {canViewSupportRequests && supportRequests?.map((request) => (
                  <div
                    key={`support-${request.id}`}
                    className={cn(
                      'w-full flex items-start gap-3 p-4 text-left transition-colors',
                      'bg-purple-50/50 dark:bg-purple-950/20'
                    )}
                  >
                    <div className={cn(
                      'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
                      'bg-purple-500/10 text-purple-600'
                    )}>
                      <Headphones className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-medium text-sm">Support Request</p>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          {formatRelativeTime(request.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5 truncate">
                        {request.requester_name || 'A cashier'} needs help
                        {request.location_name && ` at ${request.location_name}`}
                        {request.register_name && ` (${request.register_name})`}
                      </p>
                      {request.message && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 italic">
                          "{request.message}"
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          size="sm"
                          className="h-7 px-3 text-xs gap-1 bg-purple-600 hover:bg-purple-700"
                          onClick={() => {
                            setOpen(false);
                            navigate(`/backoffice/registers?support=${request.id}`);
                          }}
                        >
                          <Monitor className="h-3 w-3" />
                          Connect & Help
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs text-muted-foreground"
                          onClick={() => dismissSupportRequest(request.id)}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Regular Notifications */}
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    className={cn(
                      'w-full flex items-start gap-3 p-4 text-left hover:bg-muted/50 transition-colors cursor-pointer group',
                      !notification.read_at && 'bg-muted/30'
                    )}
                    onClick={() => handleNotificationClick(notification)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleNotificationClick(notification);
                      }
                    }}
                  >
                    <div className={cn(
                      'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
                      getNotificationColor(notification.type)
                    )}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn(
                          'text-sm',
                          !notification.read_at && 'font-medium'
                        )}>
                          {notification.title}
                        </p>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(notification.created_at)}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification.mutate(notification.id);
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {notification.message && (
                        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                      )}
                      {notification.location?.name && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {notification.location.name}
                        </p>
                      )}
                    </div>
                    {!notification.read_at && (
                      <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-2" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with link to Approvals page */}
          {can2FAApprove && pending2FACount > 0 && (
            <div className="px-4 py-3 border-t bg-muted/30">
              <Link
                to="/command-center/alerts"
                className="text-sm text-primary hover:underline"
                onClick={() => setOpen(false)}
              >
                View all approvals →
              </Link>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Device Approval Dialog */}
      <DeviceApprovalDialog
        open={!!selectedApprovalRequest}
        onOpenChange={(open) => !open && setSelectedApprovalRequest(null)}
        request={selectedApprovalRequest}
        onApproved={() => {
          // Delete the corresponding notification if it exists
          if (selectedApprovalRequest) {
            const relatedNotification = notifications.find(
              n => n.type === 'device_approval_request' && n.data?.request_id === selectedApprovalRequest.id
            );
            if (relatedNotification) {
              deleteNotification.mutate(relatedNotification.id);
            }
          }
          setSelectedApprovalRequest(null);
        }}
        onDenied={() => {
          // Delete the corresponding notification if it exists
          if (selectedApprovalRequest) {
            const relatedNotification = notifications.find(
              n => n.type === 'device_approval_request' && n.data?.request_id === selectedApprovalRequest.id
            );
            if (relatedNotification) {
              deleteNotification.mutate(relatedNotification.id);
            }
          }
          setSelectedApprovalRequest(null);
        }}
        onRegisterLimitError={(errorMessage) => {
          setSelectedApprovalRequest(null);
          setRegisterLimitError(errorMessage);
        }}
      />

      {/* Register Limit Dialog */}
      <RegisterLimitDialog
        open={!!registerLimitError}
        onOpenChange={(open) => !open && setRegisterLimitError(null)}
        errorMessage={registerLimitError || undefined}
      />
    </>
  );
}
